import { useTranslation } from 'react-i18next';
import { Flex } from '@mantine/core';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { ManifestModelSearch } from '../../components/ManifestModelSearch/ManifestModelSearch';
import { RecentModelSearchesTable } from '../../components/DataTables/RecentModelSearchesTable';
import { clearRecentModelSearches, recordModelSearch } from '../../api/models/models.api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRecentModelSearches } from '../../api/models/models.api';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  GetAiModelSearchHistoryResponseData,
  PostAiModelAnalysisRequestBody,
} from '@manifest-cyber/types/interface/contracts/model-analysis';
import { useNotifications } from '../../hooks/utils/useNotifications';
import { AtomIcon } from '../../components/Icons/AtomIcon/AtomIcon';
import styles from './index.module.scss';
import { FailedApiResponse } from '../../lib/request/rejectFailedRequest/rejectFailedRequest';

const NOTIFICATION_PENDING_TIMEOUT = 5 * 1000; // 5 seconds
const NOTIFICATION_TIMEOUT = 10 * 1000; // 10 seconds

const NotificationIcon = <AtomIcon color="#1EBCE2" className={styles.notificationIcon} />;

const AIModelExplore = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { custom: customNotification, error: errorNotification } = useNotifications();
  const [localSearches, setLocalSearches] = useState<GetAiModelSearchHistoryResponseData>(
    [],
  );

  const {
    data: recentSearchesData,
    isLoading: recentSearchesLoading,
    refetch: refetchRecentSearches,
  } = useQuery({
    queryKey: ['recent-model-searches'],
    queryFn: getRecentModelSearches,
    initialData: [],
    staleTime: 0,
    cacheTime: 0,
    refetchInterval: 5000, // 5 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const { mutate: recordSearch } = useMutation({
    mutationFn: recordModelSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-model-searches'] });
    },
    onError: ({ errors }: FailedApiResponse) => {
      errorNotification({
        title: t('page.ai-explore.notifications.modelScanError.title'),
        message: t(
          errors ? errors[0] : 'page.ai-explore.notifications.modelScanError.message',
        ),
        autoClose: NOTIFICATION_TIMEOUT,
      });
    },
  });

  const { mutate: clearRecentSearches } = useMutation({
    mutationFn: clearRecentModelSearches,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-model-searches'] });
    },
  });

  const handleSearch = async ({
    kind,
    repo,
    owner,
    revision,
    includeArxivData,
  }: PostAiModelAnalysisRequestBody & { includeArxivData: boolean }) => {
    recordSearch({
      kind,
      repo,
      owner,
      revision,
      includeArxivData,
    });
    customNotification({
      title: t('page.ai-explore.notifications.modelScanInProgress.title'),
      message: t('page.ai-explore.notifications.modelScanInProgress.message'),
      autoClose: NOTIFICATION_PENDING_TIMEOUT,
      icon: NotificationIcon,
    });
    refetchRecentSearches();
  };

  const handleClear = () => {
    clearRecentSearches();
    setLocalSearches([]);
    refetchRecentSearches();
    queryClient.invalidateQueries({ queryKey: ['recent-model-searches'] });
  };

  const modelReadyNotification = ({ id, name }: { id: string; name: string }) => {
    customNotification({
      title: t('page.ai-explore.notifications.modelScanSuccess.title'),
      message: name,
      autoClose: NOTIFICATION_TIMEOUT,
      icon: NotificationIcon,
      action: {
        label: t('page.ai-explore.notifications.modelScanSuccess.action'),
        onClick: () => {
          window.location.href = `/ai-explorer/model/${encodeURIComponent(id)}`;
        },
      },
    });
  };

  const getDiff = (
    oldData: GetAiModelSearchHistoryResponseData,
    newData: GetAiModelSearchHistoryResponseData,
  ) => {
    return newData.filter((updatedItem) => {
      const localItem = oldData.find((localItem) => localItem._id === updatedItem._id);
      return (
        localItem && localItem.status === 'running' && updatedItem.status !== 'running'
      );
    });
  };

  const hasRecentModels = recentSearchesData.length > 0;
  const diff = getDiff(localSearches, recentSearchesData);

  useEffect(() => {
    setLocalSearches(recentSearchesData);
  }, [recentSearchesData]);

  useEffect(() => {
    diff.forEach((item) => {
      if (item.status === 'completed') {
        modelReadyNotification({
          id: item._id.toString(),
          name: item.name,
        });
      } else if (item.status === 'errored') {
        errorNotification({
          title: t('page.ai-explore.notifications.modelScanError.title'),
          message: (
            <Flex direction="column">
              <TextComponent>
                {t('page.ai-explore.notifications.modelScanError.message')}
              </TextComponent>
              <TextComponent color="muted" variant="bodySmall">
                {item.name}
              </TextComponent>
            </Flex>
          ),
          autoClose: NOTIFICATION_TIMEOUT,
        });
      }
    });
  }, [diff, modelReadyNotification]);

  return (
    <Flex direction="column" mt={30}>
      <Flex direction="column" w="100%">
        <TextComponent variant="displayXLarge">
          {t('page.ai-explore.title')}
        </TextComponent>
        <TextComponent color="muted" variant="bodyMedium" pt="8px">
          {t('page.ai-explore.subtitle')}
        </TextComponent>
        <Flex pt="24px" gap="md" direction="column" w="100%">
          <ManifestModelSearch recordSearch={handleSearch} />
          <RecentModelSearchesTable
            data={localSearches}
            isLoading={recentSearchesLoading}
            handleClear={handleClear}
            showClearButton={hasRecentModels}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AIModelExplore;
